import { FC, useEffect, useMemo, useRef, useState } from 'react'
import Swal from 'sweetalert2'
import * as XLSX from 'sheetjs-style'
import jsPDF from 'jspdf'
import PasswordConfirmModal from './PasswordConfirmModal'
import { ConferenceLoadingOverlay } from './ConferenceLoadingOverlay'
import useAuth from '../../../../hooks/useAuth'
import {
  authorizeSellerQueueAction,
  filterAdminQueueSellerOrders,
  filterTotvsConferenceOrders,
  getUsersByTypeRoute,
  hasTotvsIntegrationForConference,
} from '../services'
import { ConferenceOrderFilterPayload, ConferenceOrderItem, ConferenceSeller } from '../types'

interface ConferenceTabProps {
  isPrivileged: boolean
  pluginspaceId: number
}

interface ConferenceMetricCardProps {
  columnClassName?: string
  cardClassName: string
  valueClassName: string
  value: string | number
  label: string
}

const getFileTimestamp = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  const hours = String(now.getHours()).padStart(2, '0')
  const minutes = String(now.getMinutes()).padStart(2, '0')
  const seconds = String(now.getSeconds()).padStart(2, '0')
  return `${year}${month}${day}-${hours}${minutes}${seconds}`
}

const normalizeCpf = (value: string): string => value.replace(/\D/g, '')

const normalizeCode = (value: string): string =>
  value
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')

const toObject = (value: unknown): Record<string, unknown> => {
  if (!value) return {}
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {}
    } catch {
      return {}
    }
  }

  if (typeof value === 'object') {
    return value as Record<string, unknown>
  }

  return {}
}

const pickContentValue = (content: Record<string, unknown>, keys: string[]): string => {
  for (const key of keys) {
    const raw = content[key]
    if (typeof raw === 'string' && raw.trim()) {
      return raw
    }
  }

  return ''
}

const extractSellerFields = (order: ConferenceOrderItem): { cpf: string; code: string } => {
  const content = toObject(order.content)
  return {
    cpf: pickContentValue(content, ['sellerCpf', 'cpfSeller', 'sellerCpfCnpj', 'cpfVendedor']),
    code: pickContentValue(content, ['codeSeller', 'sellerCode', 'codSeller']),
  }
}

const toUtcStartDate = (dateValue: string): string => `${dateValue}T00:00:00.000Z`
const toUtcEndDate = (dateValue: string): string => `${dateValue}T23:59:59.999Z`

const formatOrderTotal = (total?: number | null): string => {
  if (typeof total !== 'number' || Number.isNaN(total)) {
    return '-'
  }

  // In this conference flow, Ninegrid returns `total` in cents.
  const totalInReais = total / 100

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(totalInReais)
}

const sortConferenceOrders = (items: ConferenceOrderItem[]): ConferenceOrderItem[] => {
  return [...items].sort((left, right) => {
    const leftTime = new Date(left.created).getTime()
    const rightTime = new Date(right.created).getTime()

    return rightTime - leftTime
  })
}

const getOrderSourceLabel = (order: ConferenceOrderItem): string => {
  if (order.source === 'totvs') {
    return 'TOTVS'
  }

  return 'Ninegrid'
}

const getOrderSourceBadgeClass = (order: ConferenceOrderItem): string => {
  if (order.source === 'totvs') {
    return 'badge badge-light-warning'
  }

  return 'badge badge-light-primary'
}

const createTotvsCacheKey = (
  payload: ConferenceOrderFilterPayload,
  page: number,
  pageSize: number
): string => {
  return JSON.stringify({ payload, page, pageSize })
}

const ConferenceMetricCard: FC<ConferenceMetricCardProps> = ({
  columnClassName = 'col-md-3',
  cardClassName,
  valueClassName,
  value,
  label,
}) => {
  return (
    <div className={columnClassName}>
      <div className={`card ${cardClassName}`}>
        <div className='card-body py-4'>
          <div className={`fs-2 fw-bold ${valueClassName}`}>{value}</div>
          <div className='fs-7 text-gray-700'>{label}</div>
        </div>
      </div>
    </div>
  )
}

const ConferenceSellerHighlights: FC<{ withCpf: number; withCode: number }> = ({
  withCpf,
  withCode,
}) => {
  return (
    <div className='alert alert-light d-flex flex-wrap gap-6 align-items-center py-3'>
      <span className='text-gray-700'>Pedidos com CPF do vendedor: <strong>{withCpf}</strong></span>
      <span className='text-gray-700'>Pedidos com código do vendedor: <strong>{withCode}</strong></span>
    </div>
  )
}

export const ConferenceTab: FC<ConferenceTabProps> = ({ isPrivileged, pluginspaceId }) => {
  const { loginInfo } = useAuth()
  const ninegridPageSize = 20
  const consolidatedPageSize = 20
  const totvsPageSize = 500
  const [loading, setLoading] = useState(false)
  const [activeSource, setActiveSource] = useState<'ninegrid' | 'totvs' | 'consolidated'>('ninegrid')
  const [ninegridOrders, setNinegridOrders] = useState<ConferenceOrderItem[]>([])
  const [totvsOrders, setTotvsOrders] = useState<ConferenceOrderItem[]>([])
  const [sellers, setSellers] = useState<ConferenceSeller[]>([])
  const [startDate, setStartDate] = useState('')
  const [finalDate, setFinalDate] = useState('')
  const [orderSearch, setOrderSearch] = useState('')
  const [customerNameSearch, setCustomerNameSearch] = useState('')
  const [searchSource, setSearchSource] = useState<'ninegrid' | 'totvs' | 'hybrid'>('hybrid')
  const [selectedSellerId, setSelectedSellerId] = useState<number | null>(null)
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false)
  const [ninegridPage, setNinegridPage] = useState(1)
  const [totvsPage, setTotvsPage] = useState(1)
  const [totvsTotalPages, setTotvsTotalPages] = useState(1)
  const [totvsTotalItems, setTotvsTotalItems] = useState(0)
  const [consolidatedPage, setConsolidatedPage] = useState(1)
  const [totvsLoading, setTotvsLoading] = useState(false)
  const [lastPayload, setLastPayload] = useState<ConferenceOrderFilterPayload | null>(null)
  const [checkingTotvsIntegration, setCheckingTotvsIntegration] = useState(false)
  const [hasTotvsIntegration, setHasTotvsIntegration] = useState(false)
  const [totvsLoadWarning, setTotvsLoadWarning] = useState<string | null>(null)
  const latestTotvsRequestId = useRef(0)
  const totvsPageCacheRef = useRef<Record<string, { items: ConferenceOrderItem[]; totalItems: number; totalPages: number; page: number }>>({})

  const currentUserId = Number(loginInfo?.id) || 0
  const currentUserProfile = String(loginInfo?.profile || '').toLowerCase()
  const isSellerProfile = currentUserProfile === 'seller'

  useEffect(() => {
    const today = new Date()
    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000)
    setStartDate(yesterday.toISOString().split('T')[0])
    setFinalDate(today.toISOString().split('T')[0])
  }, [])

  useEffect(() => {
    const loadSellers = async () => {
      try {
        const result = await getUsersByTypeRoute('seller')
        if (isPrivileged) {
          setSellers(result)
          return
        }

        const scopedSellers = result.filter((seller) => seller.id === currentUserId)
        setSellers(scopedSellers)
        if (scopedSellers.length === 1) {
          setSelectedSellerId(scopedSellers[0].id)
        }
      } catch (error) {
        Swal.fire('Erro', 'Não foi possível carregar vendedores da loja.', 'error')
      }
    }

    loadSellers()
  }, [currentUserId, isPrivileged])

  useEffect(() => {
    const checkTotvsIntegration = async () => {
      setCheckingTotvsIntegration(true)
      try {
        const hasIntegration = await hasTotvsIntegrationForConference()
        setHasTotvsIntegration(hasIntegration)
        if (!hasIntegration) {
          setSearchSource('ninegrid')
        }
      } finally {
        setCheckingTotvsIntegration(false)
      }
    }

    checkTotvsIntegration()
  }, [])

  const selectedSeller = useMemo(
    () => sellers.find((seller) => seller.id === selectedSellerId) || null,
    [sellers, selectedSellerId]
  )

  const normalizedOrderSearch = orderSearch.trim()
  const isTotvsOrderSearchInvalid =
    searchSource === 'totvs' && normalizedOrderSearch.length > 0 && !/^\d+$/.test(normalizedOrderSearch)

  const validateFilter = (): boolean => {
    if (!startDate || !finalDate) {
      Swal.fire('Atenção', 'Preencha Data Início e Data Final.', 'warning')
      return false
    }

    if (new Date(startDate) > new Date(finalDate)) {
      Swal.fire('Atenção', 'Data Início não pode ser maior que Data Final.', 'warning')
      return false
    }

    if (!selectedSeller && !isPrivileged) {
      Swal.fire(
        'Permissão negada',
        'Sem vendedor selecionado, o filtro é permitido apenas para admin, gerente ou owner.',
        'error'
      )
      return false
    }

    if (searchSource === 'totvs' && !hasTotvsIntegration) {
      Swal.fire('Atenção', 'A integração TOTVS não está disponível para esta loja.', 'warning')
      return false
    }

    if (isTotvsOrderSearchInvalid) {
      Swal.fire(
        'Atenção',
        'Na origem TOTVS, o campo Pedido / Pedido Externo precisa ser numérico. Para código externo, selecione Híbrido ou Apenas Ninegrid.',
        'warning'
      )
      return false
    }

    return true
  }

  const openAuthorization = () => {
    if (!validateFilter()) {
      return
    }

    setIsPasswordModalOpen(true)
  }

  const buildConferencePayload = (): ConferenceOrderFilterPayload => ({
    startDate: toUtcStartDate(startDate),
    finalDate: toUtcEndDate(finalDate),
    ...(orderSearch.trim() ? { orderSearch: orderSearch.trim() } : {}),
    ...(customerNameSearch.trim() ? { customerName: customerNameSearch.trim() } : {}),
    ...(selectedSeller?.cpf ? { cpfSeller: selectedSeller.cpf } : {}),
    ...(selectedSeller?.sellerCode ? { codeSeller: selectedSeller.sellerCode } : {}),
  })

  const applySellerFilter = (sourceOrders: ConferenceOrderItem[]) => {
    if (!selectedSeller) {
      return sourceOrders
    }

    const selectedCpf = selectedSeller.cpf ? normalizeCpf(selectedSeller.cpf) : ''
    const selectedCode = selectedSeller.sellerCode ? normalizeCode(selectedSeller.sellerCode) : ''

    return sourceOrders.filter((order) => {
      const { cpf, code } = extractSellerFields(order)
      const orderCpf = cpf ? normalizeCpf(cpf) : ''
      const orderCode = code ? normalizeCode(code) : ''

      const cpfMatch = selectedCpf && orderCpf ? selectedCpf === orderCpf : false
      const codeMatch = selectedCode && orderCode ? selectedCode === orderCode : false

      return cpfMatch || codeMatch
    })
  }

  const loadTotvsOrders = async (
    basePayload: ConferenceOrderFilterPayload,
    page: number = 1
  ) => {
    if (!hasTotvsIntegration) {
      return
    }

    const requestId = latestTotvsRequestId.current + 1
    latestTotvsRequestId.current = requestId
    setTotvsLoading(true)

    const cacheKey = createTotvsCacheKey(basePayload, page, totvsPageSize)
    const cached = totvsPageCacheRef.current[cacheKey]
    if (cached) {
      setTotvsOrders(cached.items)
      setTotvsPage(cached.page)
      setTotvsTotalItems(cached.totalItems)
      setTotvsTotalPages(cached.totalPages)
      setTotvsLoading(false)
      return
    }

    try {
      const result = await filterTotvsConferenceOrders(basePayload, page, totvsPageSize)
      if (requestId !== latestTotvsRequestId.current) {
        return
      }

      const filteredTotvs = applySellerFilter(result.items)
      const normalizedTotalItems =
        result.meta.totalItems > 0
          ? result.meta.totalItems
          : page === 1
            ? filteredTotvs.length
            : totvsTotalItems
      const normalizedTotalPages = Math.max(
        1,
        result.meta.totalPages > 0
          ? result.meta.totalPages
          : Math.ceil(normalizedTotalItems / totvsPageSize) || 1
      )

      setTotvsOrders(sortConferenceOrders(filteredTotvs))
      setTotvsPage(Math.max(1, result.meta.page || page))
      setTotvsTotalItems(normalizedTotalItems)
      setTotvsTotalPages(normalizedTotalPages)

      totvsPageCacheRef.current[cacheKey] = {
        items: sortConferenceOrders(filteredTotvs),
        page: Math.max(1, result.meta.page || page),
        totalItems: normalizedTotalItems,
        totalPages: normalizedTotalPages,
      }
    } catch (totvsError) {
      console.warn('Falha ao carregar pedidos da TOTVS na conferência:', totvsError)
      setTotvsLoadWarning('A TOTVS não respondeu nesta consulta. Os pedidos do Ninegrid continuam exibidos normalmente.')
    } finally {
      if (requestId === latestTotvsRequestId.current) {
        setTotvsLoading(false)
      }
    }
  }

  const handleRetryTotvs = async () => {
    if (!hasTotvsIntegration || !lastPayload) {
      return
    }

    setLoading(true)
    setTotvsLoadWarning(null)
    try {
      await loadTotvsOrders(lastPayload, totvsPage)
    } finally {
      setLoading(false)
    }
  }

  const applyFilter = async (credential: string) => {
    const actionType = selectedSeller
      ? 'conference_search_seller'
      : 'conference_search_all'

    const auth = await authorizeSellerQueueAction(
      actionType,
      credential,
      selectedSeller?.id
    )

    if (!auth.authorized) {
      Swal.fire('Permissão negada', auth.message || 'Credencial sem permissão.', 'error')
      return
    }

    const basePayload = buildConferencePayload()
    const normalizedOrderSearch = (basePayload.orderSearch || '').trim()
    const hasOrderSearch = normalizedOrderSearch.length > 0
    const hasNumericOrderSearch = /^\d+$/.test(normalizedOrderSearch)
    const shouldFetchNinegrid = searchSource !== 'totvs'
    const shouldFetchTotvsBySource = searchSource !== 'ninegrid' && hasTotvsIntegration
    const shouldFetchTotvs =
      shouldFetchTotvsBySource && (!hasOrderSearch || hasNumericOrderSearch)

    setLastPayload(shouldFetchTotvs ? basePayload : null)
    totvsPageCacheRef.current = {}

    setLoading(true)
    setTotvsLoadWarning(null)
    try {
      if (shouldFetchNinegrid) {
        const ninegridOrders = await filterAdminQueueSellerOrders(basePayload)
        const filteredNinegridOrders = applySellerFilter(ninegridOrders)
        setNinegridOrders(sortConferenceOrders(filteredNinegridOrders))
      } else {
        setNinegridOrders([])
      }

      setNinegridPage(1)
      setConsolidatedPage(1)

      if (shouldFetchTotvs) {
        setTotvsPage(1)
        await loadTotvsOrders(basePayload, 1)
      } else {
        setTotvsOrders([])
        setTotvsTotalItems(0)
        setTotvsTotalPages(1)

        if ((searchSource === 'hybrid' || searchSource === 'totvs') && hasOrderSearch && !hasNumericOrderSearch) {
          const warningMessage =
            searchSource === 'hybrid'
              ? 'Na TOTVS, o filtro de Pedido deve ser numérico. A busca foi aplicada apenas no Ninegrid para este campo.'
              : 'Na TOTVS, o filtro de Pedido deve ser numérico. Ajuste o valor para executar a consulta TOTVS.'
          setTotvsLoadWarning(warningMessage)
        } else if (searchSource === 'hybrid' && !hasTotvsIntegration) {
          setTotvsLoadWarning('A integração TOTVS não está disponível. A consulta foi feita apenas no Ninegrid.')
        }
      }

      if (searchSource === 'ninegrid') {
        setActiveSource('ninegrid')
      } else if (searchSource === 'totvs') {
        setActiveSource('totvs')
      } else {
        setActiveSource('consolidated')
      }
    } catch (error) {
      const message = typeof error === 'string' ? error : 'Erro ao filtrar pedidos da conferência.'
      Swal.fire('Erro', message, 'error')
    } finally {
      setLoading(false)
    }
  }

  const getStatusLabel = (order: ConferenceOrderItem): string => {
    return order.statusLabel || order.status?.[0]?.name || '-'
  }

  const getStatusOrderType = (order: ConferenceOrderItem): string => {
    return order.statusOrderType || '-'
  }

  const consolidatedOrders = useMemo(
    () => sortConferenceOrders([...ninegridOrders, ...totvsOrders]),
    [ninegridOrders, totvsOrders]
  )

  const summary = useMemo(() => {
    let withCpf = 0
    let withCode = 0
    let totalAmountCents = 0
    let ninegridCount = ninegridOrders.length
    let totvsCount = totvsTotalItems

    for (const order of consolidatedOrders) {
      const fields = extractSellerFields(order)
      if (fields.cpf) withCpf += 1
      if (fields.code) withCode += 1
      if (typeof order.total === 'number' && !Number.isNaN(order.total)) {
        totalAmountCents += order.total
      }
    }

    return {
      total: ninegridOrders.length + totvsTotalItems,
      withCpf,
      withCode,
      ninegridCount,
      totvsCount,
      totalAmountCents,
    }
  }, [consolidatedOrders, ninegridOrders.length, totvsTotalItems])

  const ninegridTotalPages = Math.max(1, Math.ceil(ninegridOrders.length / ninegridPageSize))
  const pagedNinegridOrders = useMemo(() => {
    const startIndex = (ninegridPage - 1) * ninegridPageSize
    return ninegridOrders.slice(startIndex, startIndex + ninegridPageSize)
  }, [ninegridOrders, ninegridPage])

  const consolidatedTotalPages = Math.max(
    1,
    Math.ceil(consolidatedOrders.length / consolidatedPageSize)
  )
  const pagedConsolidatedOrders = useMemo(() => {
    const startIndex = (consolidatedPage - 1) * consolidatedPageSize
    return consolidatedOrders.slice(startIndex, startIndex + consolidatedPageSize)
  }, [consolidatedOrders, consolidatedPage])

  const displayedOrders = useMemo(() => {
    if (activeSource === 'totvs') {
      return totvsOrders
    }
    if (activeSource === 'consolidated') {
      return pagedConsolidatedOrders
    }
    return pagedNinegridOrders
  }, [activeSource, pagedConsolidatedOrders, pagedNinegridOrders, totvsOrders])

  const exportOrders = useMemo(() => {
    if (activeSource === 'totvs') {
      return totvsOrders
    }
    if (activeSource === 'consolidated') {
      return consolidatedOrders
    }
    return ninegridOrders
  }, [activeSource, consolidatedOrders, ninegridOrders, totvsOrders])

  const handlePrevNinegridPage = () => setNinegridPage((prev) => Math.max(1, prev - 1))
  const handleNextNinegridPage = () =>
    setNinegridPage((prev) => Math.min(ninegridTotalPages, prev + 1))

  const handlePrevConsolidatedPage = () =>
    setConsolidatedPage((prev) => Math.max(1, prev - 1))
  const handleNextConsolidatedPage = () =>
    setConsolidatedPage((prev) => Math.min(consolidatedTotalPages, prev + 1))

  const handlePrevTotvsPage = () => {
    if (!lastPayload || totvsPage <= 1 || totvsLoading) {
      return
    }
    void loadTotvsOrders(lastPayload, totvsPage - 1)
  }

  const handleNextTotvsPage = () => {
    if (!lastPayload || totvsPage >= totvsTotalPages || totvsLoading) {
      return
    }
    void loadTotvsOrders(lastPayload, totvsPage + 1)
  }

  const handleExportExcel = () => {
    if (exportOrders.length === 0) return

    const rows = exportOrders.map((order) => {
      const sellerFields = extractSellerFields(order)
      return {
        NumeroPedido: order.id,
        PedidoExterno: order.orderidExt || '-',
        Origem: getOrderSourceLabel(order),
        Cliente: order.customer?.name || '-',
        ValorPedido: formatOrderTotal(order.total),
        Data: new Date(order.created).toLocaleString('pt-BR'),
        Status: getStatusLabel(order),
        TipoStatus: getStatusOrderType(order),
        CpfVendedor: sellerFields.cpf || '-',
        CodigoVendedor: sellerFields.code || '-',
      }
    })

    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'ConferenciaPedidos')
    const timestamp = getFileTimestamp()
    XLSX.writeFile(wb, `seller-queue-conferencia-ps-${pluginspaceId}-${timestamp}.xlsx`)
  }

  const handleExportPdf = () => {
    if (exportOrders.length === 0) return

    try {
      const doc = new jsPDF({ orientation: 'landscape' })
      const jsPDFAutoTable = require('jspdf-autotable')

      doc.setFontSize(14)
      doc.text('Pedidos para Conferencia', 14, 15)
      doc.setFontSize(10)
      doc.text(`Periodo: ${startDate || '-'} ate ${finalDate || '-'}`, 14, 22)

      jsPDFAutoTable.autoTable(doc, {
        startY: 28,
        head: [[
          'Num. Pedido',
          'Pedido Ext.',
          'Origem',
          'Cliente',
          'Valor',
          'Data',
          'Status',
          'Tipo Status',
          'CPF Vendedor',
          'Codigo Vendedor',
        ]],
        body: exportOrders.map((order) => {
          const sellerFields = extractSellerFields(order)
          return [
            String(order.id),
            order.orderidExt || '-',
            getOrderSourceLabel(order),
            order.customer?.name || '-',
            formatOrderTotal(order.total),
            new Date(order.created).toLocaleString('pt-BR'),
            getStatusLabel(order),
            getStatusOrderType(order),
            sellerFields.cpf || '-',
            sellerFields.code || '-',
          ]
        }),
        theme: 'grid',
        headStyles: { fillColor: [13, 110, 253], textColor: 255 },
      })

      const timestamp = getFileTimestamp()
      doc.save(`seller-queue-conferencia-ps-${pluginspaceId}-${timestamp}.pdf`)
    } catch (error) {
      console.error('Erro ao exportar PDF da conferencia:', error)
    }
  }

  return (
    <>
      <ConferenceLoadingOverlay visible={loading} />
      <div>
      <div className='card mb-5'>
        <div className='card-header'>
          <h3 className='card-title'>Filtros da Conferência</h3>
        </div>
        <div className='card-body'>
          <div className='mb-4'>
            {checkingTotvsIntegration ? (
              <div className='alert alert-secondary py-3 mb-0'>
                Verificando integração TOTVS...
              </div>
            ) : hasTotvsIntegration ? (
              <div className='alert alert-info py-3 mb-0'>
                Integração TOTVS detectada (plugintypeId=17 e plugintemplateId=23).
                A busca de pedidos TOTVS será habilitada nesta aba.
              </div>
            ) : (
              <div className='alert alert-light py-3 mb-0'>
                Nenhuma integração TOTVS detectada para esta loja.
              </div>
            )}
          </div>

          {totvsLoadWarning ? (
            <div className='alert alert-warning d-flex flex-wrap justify-content-between align-items-center gap-3 py-3 mb-4'>
              <span>{totvsLoadWarning}</span>
              <button className='btn btn-sm btn-warning' type='button' onClick={handleRetryTotvs}>
                Tentar novamente TOTVS
              </button>
            </div>
          ) : null}

          {activeSource === 'totvs' && lastPayload ? (
            <div className='alert alert-light-info py-3 mb-4'>
              Paginação TOTVS ativa em modo server-side (pageSize fixo em {totvsPageSize}).
              Ao navegar nas páginas, a consulta é refeita na TOTVS com os mesmos filtros.
            </div>
          ) : null}

          <div className='row g-3'>
            <div className='col-md-3'>
              <label className='form-label'>Data Início</label>
              <input
                type='date'
                className='form-control'
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className='col-md-3'>
              <label className='form-label'>Data Final</label>
              <input
                type='date'
                className='form-control'
                value={finalDate}
                onChange={(e) => setFinalDate(e.target.value)}
              />
            </div>
            <div className='col-md-3'>
              <label className='form-label'>Pedido / Pedido Externo</label>
              <input
                type='text'
                className={`form-control ${isTotvsOrderSearchInvalid ? 'border border-danger' : ''}`}
                value={orderSearch}
                onChange={(e) => setOrderSearch(e.target.value)}
                placeholder='Número do pedido ou código externo'
              />
              <div className='form-text'>
                Híbrido e Ninegrid aceitam pedido ou código externo; TOTVS exige pedido numérico.
              </div>
              {isTotvsOrderSearchInvalid ? (
                <div className='form-text text-danger'>
                  Na origem TOTVS, use somente número no campo Pedido / Pedido Externo.
                </div>
              ) : null}
            </div>
            <div className='col-md-3'>
              <label className='form-label'>Cliente</label>
              <input
                type='text'
                className='form-control'
                value={customerNameSearch}
                onChange={(e) => setCustomerNameSearch(e.target.value)}
                placeholder='Nome do cliente'
              />
            </div>
            <div className='col-md-3'>
              {hasTotvsIntegration ? (
                <>
                  <label className='form-label'>Origem</label>
                  <select
                    className='form-select'
                    value={searchSource}
                    onChange={(e) =>
                      setSearchSource(e.target.value as 'ninegrid' | 'totvs' | 'hybrid')
                    }
                  >
                    <option value='hybrid'>Híbrido (Ninegrid + TOTVS)</option>
                    <option value='ninegrid'>Apenas Ninegrid</option>
                    <option value='totvs'>Apenas TOTVS</option>
                  </select>
                </>
              ) : (
                <>
                  <label className='form-label'>Origem</label>
                  <div className='form-control bg-light text-muted d-flex align-items-center'>
                    Ninegrid
                  </div>
                  <div className='form-text'>
                    Esta loja não possui integração TOTVS detectada, então a origem permanece em Ninegrid.
                  </div>
                </>
              )}
            </div>
            <div className='col-md-6'>
              <label className='form-label'>Vendedor</label>
              <select
                className='form-select'
                value={selectedSellerId || ''}
                onChange={(e) =>
                  setSelectedSellerId(e.target.value ? Number(e.target.value) : null)
                }
                disabled={isSellerProfile && sellers.length <= 1}
              >
                <option value=''>Todos os vendedores</option>
                {sellers.map((seller) => (
                  <option key={seller.id} value={seller.id}>
                    {seller.name}
                    {seller.sellerCode ? ` (${seller.sellerCode})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className='col-md-4 d-flex align-items-end'>
              <div className='d-flex align-items-center gap-2 w-100'>
                <button className='btn btn-primary flex-grow-1' onClick={openAuthorization}>
                  <i className='fas fa-search me-2'></i>
                  Filtrar
                </button>
                <button
                  className='btn btn-light-success d-flex align-items-center justify-content-center'
                  style={{ width: 38, height: 38, padding: 0 }}
                  onClick={handleExportExcel}
                  disabled={exportOrders.length === 0}
                  title='Exportar para Excel'
                  aria-label='Exportar para Excel'
                >
                  <i className='fas fa-file-excel'></i>
                </button>
                <button
                  className='btn btn-light-danger d-flex align-items-center justify-content-center'
                  style={{ width: 38, height: 38, padding: 0 }}
                  onClick={handleExportPdf}
                  disabled={exportOrders.length === 0}
                  title='Exportar para PDF'
                  aria-label='Exportar para PDF'
                >
                  <i className='fas fa-file-pdf'></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='card'>
        <div className='card-header'>
          <h3 className='card-title'>Pedidos para Conferência</h3>
        </div>
        <div className='card-body'>
          <div className='d-flex align-items-center gap-2 mb-4'>
            <button
              type='button'
              className={`btn btn-sm ${activeSource === 'ninegrid' ? 'btn-primary' : 'btn-light-primary'}`}
              onClick={() => setActiveSource('ninegrid')}
            >
              Ninegrid
            </button>
            <button
              type='button'
              className={`btn btn-sm ${activeSource === 'totvs' ? 'btn-warning' : 'btn-light-warning'}`}
              onClick={() => setActiveSource('totvs')}
              disabled={!hasTotvsIntegration}
            >
              TOTVS
            </button>
            <button
              type='button'
              className={`btn btn-sm ${activeSource === 'consolidated' ? 'btn-info' : 'btn-light-info'}`}
              onClick={() => setActiveSource('consolidated')}
            >
              Consolidado
            </button>
          </div>

          {activeSource === 'totvs' && totvsLoading ? (
            <div className='alert alert-warning py-3'>Carregando pedidos TOTVS da página {totvsPage}...</div>
          ) : null}

          <div className='row g-3 mb-4'>
            <ConferenceMetricCard
              cardClassName='bg-light-primary'
              valueClassName='text-primary'
              value={summary.total}
              label='Total de pedidos'
            />
            <ConferenceMetricCard
              cardClassName='bg-light-warning'
              valueClassName='text-warning'
              value={formatOrderTotal(summary.totalAmountCents)}
              label='Valor dos pedidos carregados'
            />
            <ConferenceMetricCard
              cardClassName='bg-light-success'
              valueClassName='text-success'
              value={summary.ninegridCount}
              label='Pedidos do Ninegrid'
            />
            <ConferenceMetricCard
              cardClassName='bg-light-info'
              valueClassName='text-info'
              value={summary.totvsCount}
              label='Pedidos da TOTVS'
            />
          </div>

          <ConferenceSellerHighlights withCpf={summary.withCpf} withCode={summary.withCode} />

          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 gy-7'>
              <thead>
                <tr className='fw-bold fs-6 text-gray-800'>
                  <th>Num. Pedido</th>
                  <th>Pedido Ext.</th>
                  <th>Origem</th>
                  <th>Cliente</th>
                  <th>Valor do Pedido</th>
                  <th>Data</th>
                  <th>Status</th>
                  <th>Tipo Status</th>
                  <th>CPF Vendedor</th>
                  <th>Código Vendedor</th>
                </tr>
              </thead>
              <tbody>
                {displayedOrders.length === 0 ? (
                  <tr>
                    <td colSpan={10} className='text-center py-5'>
                      Nenhum pedido encontrado para os filtros informados.
                    </td>
                  </tr>
                ) : (
                  displayedOrders.map((order) => {
                    const sellerFields = extractSellerFields(order)
                    return (
                      <tr key={order.id}>
                        <td className='fw-bold'>{order.id}</td>
                        <td>{order.orderidExt || '-'}</td>
                        <td>
                          <span className={getOrderSourceBadgeClass(order)}>
                            {getOrderSourceLabel(order)}
                          </span>
                        </td>
                        <td>{order.customer?.name || '-'}</td>
                        <td>{formatOrderTotal(order.total)}</td>
                        <td>{new Date(order.created).toLocaleString('pt-BR')}</td>
                        <td>{getStatusLabel(order)}</td>
                        <td>{getStatusOrderType(order)}</td>
                        <td>{sellerFields.cpf || '-'}</td>
                        <td>{sellerFields.code || '-'}</td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>

          {activeSource === 'ninegrid' && ninegridOrders.length > 0 ? (
            <div className='d-flex align-items-center justify-content-between mt-4'>
              <div className='text-muted fs-7'>
                Exibindo {(ninegridPage - 1) * ninegridPageSize + 1} a{' '}
                {Math.min(ninegridPage * ninegridPageSize, ninegridOrders.length)} de {ninegridOrders.length} pedidos
              </div>
              <div className='d-flex align-items-center gap-2'>
                <button className='btn btn-light btn-sm' onClick={handlePrevNinegridPage} disabled={ninegridPage === 1}>
                  Anterior
                </button>
                <span className='fw-semibold fs-7'>
                  Página {ninegridPage} de {ninegridTotalPages}
                </span>
                <button
                  className='btn btn-light btn-sm'
                  onClick={handleNextNinegridPage}
                  disabled={ninegridPage >= ninegridTotalPages}
                >
                  Próxima
                </button>
              </div>
            </div>
          ) : null}

          {activeSource === 'totvs' && hasTotvsIntegration && (totvsOrders.length > 0 || totvsTotalItems > 0) ? (
            <div className='d-flex align-items-center justify-content-between mt-4'>
              <div className='text-muted fs-7'>
                Exibindo página {totvsPage} de {totvsTotalPages} ({totvsTotalItems} pedidos na TOTVS)
              </div>
              <div className='d-flex align-items-center gap-2'>
                <button className='btn btn-light btn-sm' onClick={handlePrevTotvsPage} disabled={totvsPage === 1 || totvsLoading}>
                  Anterior
                </button>
                <span className='fw-semibold fs-7'>
                  Página {totvsPage} de {totvsTotalPages}
                </span>
                <button
                  className='btn btn-light btn-sm'
                  onClick={handleNextTotvsPage}
                  disabled={totvsPage >= totvsTotalPages || totvsLoading}
                >
                  Próxima
                </button>
              </div>
            </div>
          ) : null}

          {activeSource === 'consolidated' && consolidatedOrders.length > 0 ? (
            <div className='d-flex align-items-center justify-content-between mt-4'>
              <div className='text-muted fs-7'>
                Exibindo {(consolidatedPage - 1) * consolidatedPageSize + 1} a{' '}
                {Math.min(consolidatedPage * consolidatedPageSize, consolidatedOrders.length)} de {consolidatedOrders.length} pedidos carregados
              </div>
              <div className='d-flex align-items-center gap-2'>
                <button
                  className='btn btn-light btn-sm'
                  onClick={handlePrevConsolidatedPage}
                  disabled={consolidatedPage === 1}
                >
                  Anterior
                </button>
                <span className='fw-semibold fs-7'>
                  Página {consolidatedPage} de {consolidatedTotalPages}
                </span>
                <button
                  className='btn btn-light btn-sm'
                  onClick={handleNextConsolidatedPage}
                  disabled={consolidatedPage >= consolidatedTotalPages}
                >
                  Próxima
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <PasswordConfirmModal
        isOpen={isPasswordModalOpen}
        title='Autorização para Conferência'
        description={
          selectedSeller
            ? 'Informe senha do vendedor selecionado ou de admin, gerente, owner ou administrador para filtrar.'
            : 'Informe senha de admin, gerente, owner ou administrador para filtrar todos os vendedores.'
        }
        onCancel={() => {
          setIsPasswordModalOpen(false)
        }}
        onConfirm={async (password) => {
          setIsPasswordModalOpen(false)
          await applyFilter(password)
        }}
      />
    </div>
    </>
  )
}
